/**
 * Reads a PDF417 barcode from an image in the browser.
 * This replaces Ambir's paid barcode license: the scanner still supplies the
 * picture, and this decoder turns the barcode in that picture into text.
 */

const READ_OPTIONS = {
  formats: ['PDF417'],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  tryDenoise: true,
  maxNumberOfSymbols: 8,
  // Plain keeps the line breaks between AAMVA fields. The default human-readable
  // mode removes them, and then the name and birth date cannot be found.
  textMode: 'Plain',
};

let readerPromise = null;

function wasmUrl() {
  const base = process.env.PUBLIC_URL || '';
  return `${base}/zxing/zxing_reader.wasm`;
}

async function loadReader() {
  if (!readerPromise) {
    readerPromise = import('zxing-wasm/reader').then((zxing) => {
      const prepare = zxing.prepareZXingModule({
        overrides: typeof window === 'undefined' ? {} : {
          locateFile: (path, prefix) => (
            String(path).endsWith('.wasm') ? wasmUrl() : `${prefix}${path}`
          ),
        },
        fireImmediately: true,
      });
      return Promise.resolve(prepare).then(() => zxing.readBarcodes);
    }).catch((error) => {
      readerPromise = null;
      throw error;
    });
  }
  return readerPromise;
}

function barcodeText(result) {
  if (!result || result.error) return '';
  return String(result.text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
}

export async function decodePdf417Texts(source, readBarcodes = null) {
  if (!source) return [];
  try {
    const read = readBarcodes || await loadReader();
    const found = [];
    const passes = [READ_OPTIONS, { ...READ_OPTIONS, binarizer: 'GlobalHistogram' }];
    for (const options of passes) {
      const results = await read(source, options);
      (results || []).forEach((result) => {
        const text = barcodeText(result);
        if (text && !found.includes(text)) found.push(text);
      });
      if (found.length) break;
    }
    return found;
  } catch (error) {
    return [];
  }
}

function base64ToBlob(base64, mimeType) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType || 'image/jpeg' });
}

function normalizeBarcodeText(value) {
  return String(value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
}

function hasLicenseText(barcode) {
  return /DCS|DAC|DBB|DAQ/.test(String(barcode?.parsedData || barcode?.text || ''));
}

async function decodeWithBarcodeDetector(source) {
  const Detector = typeof window !== 'undefined' ? window.BarcodeDetector : null;
  if (!source || typeof Detector !== 'function' || typeof createImageBitmap !== 'function') return [];
  let detector;
  try {
    detector = new Detector({ formats: ['pdf417'] });
  } catch (error) {
    try {
      detector = new Detector();
    } catch (inner) {
      return [];
    }
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(source);
    const codes = await detector.detect(bitmap);
    return (codes || []).map((code) => normalizeBarcodeText(code?.rawValue)).filter(Boolean);
  } catch (error) {
    return [];
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

async function downscaleBlob(blob, maxEdge = 1600) {
  if (!blob || typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  let bitmap;
  try {
    bitmap = await createImageBitmap(blob);
    const edge = Math.max(bitmap.width || 0, bitmap.height || 0);
    if (!edge || edge <= maxEdge) return null;
    const scale = maxEdge / edge;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve) => canvas.toBlob((next) => resolve(next), 'image/jpeg', 0.92));
  } catch (error) {
    return null;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

// Read a smaller copy first. A full 600 DPI page makes the barcode reader
// wait several seconds, and the smaller copy still contains the barcode.
export async function readLicenseBarcodeTexts(source, decode = decodePdf417Texts, nativeDecode = decodeWithBarcodeDetector) {
  if (!source) return [];
  try {
    const smaller = await downscaleBlob(source, 1400);
    const first = smaller || source;
    const native = await nativeDecode(first);
    if (native.length) return native;
    const zxing = await decode(first);
    if (zxing.length || !smaller) return zxing;
    const nativeFull = await nativeDecode(source);
    if (nativeFull.length) return nativeFull;
    return await decode(source);
  } catch (error) {
    return [];
  }
}

export async function barcodesFromImage(image, decode = decodePdf417Texts, nativeDecode = decodeWithBarcodeDetector) {
  const existing = Array.isArray(image?.barcodes) ? image.barcodes : [];
  if (existing.some(hasLicenseText)) return existing;
  if (!image?.base64Data) return existing;
  try {
    const texts = await readLicenseBarcodeTexts(
      base64ToBlob(image.base64Data, image.mimeType),
      decode,
      nativeDecode,
    );
    if (!texts.length) return existing;
    return texts.map((text) => ({
      text,
      barcodeType: 'PDF_417',
      isAamva: /ANSI|DCS|DAQ/.test(text),
    }));
  } catch (error) {
    return existing;
  }
}
